import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type { Attachment } from '../types/attachment';
import type { Company, CompanyResponse, UpdateCompanyData } from '../types/company';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getCompany = async (): Promise<Company> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.COMPANY.BASE}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch company profile');
  }

  const result: CompanyResponse = await response.json();
  return result.data;
};

export const updateCompany = async (data: UpdateCompanyData): Promise<Company> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.COMPANY.BASE}`, {
    method: 'PATCH',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update company profile');
  }

  const result: CompanyResponse = await response.json();
  return result.data;
};

export const uploadCompanyLogo = async (file: File): Promise<Attachment> => {
  if (!(file instanceof File) || file.size === 0) {
    throw new Error('No file selected.');
  }

  // Laravel does not populate uploaded files on real PUT multipart requests.
  // Send POST + method spoof so PHP parses `file` and the route still matches PUT.
  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('_method', 'PUT');

  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.COMPANY.LOGO}`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to upload company logo');
  }

  const result = await response.json();
  return (result.data ?? result) as Attachment;
};

export const removeCompanyLogo = async (): Promise<void> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.COMPANY.LOGO}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to remove company logo');
  }
};
